import { useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListSubheader,
  Typography,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Skeleton,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  HomeOutlined,
  Search as SearchIcon,
  Work as WorkIcon,
  Chat as ChatIcon,
  MenuBook as MenuBookIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { NavLink, useLocation } from "react-router-dom";
import { useAuthentication } from "@/common-hooks/use-authentication";
import type { MainLayoutProperties } from "@/types/application/common/components/main-layout/layout";
import { PERMISSIONS } from "@/common-constants/permissions";
import { ComingSoonModal } from "@/common-components/coming-soon-modal";
import TimeDisplay from "@/common-components/time-display/time-display";

const drawerWidth = 300;

export default function Layout({ children }: MainLayoutProperties) {
  const { user, loading, logout, hasPermission } = useAuthentication();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "#f9f8f5",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={200} />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={60} />
            ))}
          </Stack>
        </Box>
      </Box>
    );
  }

  const canManageUsers = hasPermission(PERMISSIONS.USER_MANAGEMENT);
  const canAccessAudit = hasPermission(PERMISSIONS.AUDIT_LOGS);
  const canAccessTransactions = hasPermission(PERMISSIONS.TRANSACTIONS_IN_LOG_ORACLE);
  const canAccessJobs = hasPermission(PERMISSIONS.JOBS_IN_LOG_ORACLE);
  const canAccessKB = hasPermission(PERMISSIONS.R_KB_IN_LOG_ORACLE);
  const canAccessAIChat = hasPermission(PERMISSIONS.AI_CHAT_IN_LOG_ORACLE);

  const mainNavItems = [
    { path: "/", label: "Dashboard", icon: <DashboardIcon /> },
    ...(canManageUsers
      ? [
          { path: "/users", label: "Users", icon: <PeopleIcon /> },
          { path: "/roles", label: "Roles", icon: <SecurityIcon /> },
        ]
      : []),
    ...(canAccessAudit
      ? [{ path: "/audit", label: "Audit", icon: <HistoryIcon /> }]
      : []),
  ];

  const logOracleNavItems = [
    ...(canAccessTransactions
      ? [{ path: "/log-oracle/transaction-lookup", label: "Transaction Lookup", icon: <SearchIcon /> }]
      : []),
    ...(canAccessJobs
      ? [{ path: "/log-oracle/jobs", label: "Jobs", icon: <WorkIcon /> }]
      : []),
    ...(canAccessAIChat
      ? [{ path: "/log-oracle/ai-chat", label: "AI Support Assistant", icon: <ChatIcon /> }]
      : []),
    ...(canAccessKB
      ? [{ path: "/log-oracle/ai-kb", label: "AI Knowledge", icon: <MenuBookIcon /> }]
      : []),
  ];

  const isActive = (path: string) => {
    return path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#23395d",
        color: "#fff",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          background: "rgba(255, 255, 255, 0.02)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            component="img"
            src="/logo-primary.png"
            alt="BCP Wizard Ops Portal Logo"
            sx={{
              width: 40,
              height: 40,
              objectFit: "contain",
              mr: 1.5,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              fontSize={"1.2rem"}
              sx={{ color: "#fff", fontWeight: 600 }}
            >
              BCP Wizard Ops Portal
            </Typography>
            {user && (
              <Typography
                fontSize={"1rem"}
                sx={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                {user.name || user.email.split("@")[0]}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                mt: 0.5,
                justifyContent: "flex-start",
                flexWrap: "wrap",
                opacity: 0.9,
              }}
            >
              <TimeDisplay variant="sidebar" />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            mt: 0.2,
            justifyContent: "left",
            flexWrap: "wrap",
            ml: 6,
          }}
        >
          <Chip
            sx={{
              color: "white",
              borderColor: "rgba(255, 255, 255, 0.3)",
              "& .MuiChip-icon": {
                color: "white !important",
                marginLeft: "12px !important",
              },
            }}
            icon={<HomeOutlined />}
            onClick={() => (window.location.href = "/")}
            variant="outlined"
            label="Home"
            size="small"
          />
          {user && (
            <Chip
              sx={{
                ml: 0.5,
                color: "white",
                borderColor: "rgba(255, 255, 255, 0.3)",
                "& .MuiChip-icon": {
                  color: "white !important",
                  marginLeft: "12px !important",
                },
              }}
              icon={<LogoutIcon />}
              onClick={handleLogout}
              variant="outlined"
              label="Logout"
              size="small"
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          overflow: "auto",
          flex: 1,
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <List
          component="nav"
          subheader={
            <ListSubheader
              component="div"
              sx={{
                background: "transparent",
                color: "rgba(255, 255, 255, 0.7) !important",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "1px",
                padding: "16px 24px 8px",
                lineHeight: 1,
              }}
            >
              Navigation
            </ListSubheader>
          }
          sx={{ mb: 0.5, py: 0 }}
        >
          {mainNavItems.map((item) => (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              onClick={() => isMobile && setMobileOpen(false)}
              selected={isActive(item.path)}
              sx={{
                margin: "2px 8px",
                borderRadius: 0,
                padding: "12px 16px",
                minHeight: 48,
                "&.Mui-selected": {
                  background:
                    "linear-gradient(90deg, rgba(0, 0, 0, 0.01) 0%, rgba(255, 255, 255, 0.2) 150%)",
                  borderRight: "3px solid #ff4f1f",
                  "& .MuiListItemIcon-root": {
                    color: "#ff4f1f",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  transform: "translateX(4px)",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "& .MuiListItemIcon-root": {
                  minWidth: 24,
                  marginRight: 2,
                  color: "rgba(255, 255, 255, 0.9)",
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  color: "inherit",
                }}
              />
            </ListItemButton>
          ))}
        </List>
        {logOracleNavItems.length > 0 && (
          <>
            <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", mx: 2, my: 1 }} />
            <List
              component="nav"
              subheader={
                <ListSubheader
                  component="div"
                  sx={{
                    background: "transparent",
                    color: "rgba(255, 255, 255, 0.7) !important",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    padding: "16px 24px 8px",
                    lineHeight: 1,
                  }}
                >
                  Log Oracle
                </ListSubheader>
              }
              sx={{ mb: 0.5, py: 0 }}
            >
              {logOracleNavItems.map((item) => (
                <ListItemButton
                  key={item.path}
                  component={NavLink}
                  to={item.path}
                  onClick={() => isMobile && setMobileOpen(false)}
                  selected={isActive(item.path)}
                  sx={{
                    margin: "2px 8px",
                    borderRadius: 0,
                    padding: "12px 16px",
                    minHeight: 48,
                    "&.Mui-selected": {
                      background:
                        "linear-gradient(90deg, rgba(0, 0, 0, 0.01) 0%, rgba(255, 255, 255, 0.2) 150%)",
                      borderRight: "3px solid #ff4f1f",
                      "& .MuiListItemIcon-root": {
                        color: "#ff4f1f",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      transform: "translateX(4px)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "& .MuiListItemIcon-root": {
                      minWidth: 24,
                      marginRight: 2,
                      color: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      color: "inherit",
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", mx: 2, my: 1 }} />
        <List
          component="nav"
          subheader={
            <ListSubheader
              component="div"
              sx={{
                background: "transparent",
                color: "rgba(255, 255, 255, 0.7) !important",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "1px",
                padding: "16px 24px 8px",
                lineHeight: 1,
              }}
            >
              Help
            </ListSubheader>
          }
          sx={{ mb: 0.5, py: 0 }}
        >
          <ListItemButton
            component={NavLink}
            to="/user-guide"
            onClick={() => isMobile && setMobileOpen(false)}
            selected={isActive("/user-guide")}
            sx={{
              margin: "2px 8px",
              borderRadius: 0,
              padding: "12px 16px",
              minHeight: 48,
              "&.Mui-selected": {
                background:
                  "linear-gradient(90deg, rgba(0, 0, 0, 0.01) 0%, rgba(255, 255, 255, 0.2) 150%)",
                borderRight: "3px solid #ff4f1f",
                "& .MuiListItemIcon-root": {
                  color: "#ff4f1f",
                },
              },
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                transform: "translateX(4px)",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "& .MuiListItemIcon-root": {
                minWidth: 24,
                marginRight: 2,
                color: "rgba(255, 255, 255, 0.9)",
              },
            }}
          >
            <ListItemIcon>
              <HelpOutlineIcon />
            </ListItemIcon>
            <ListItemText
              primary="User Guide"
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: "0.875rem",
                color: "inherit",
              }}
            />
          </ListItemButton>
        </List>
      </Box>

      <Box
        sx={{
          mt: "auto",
          p: 2,
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Typography
          variant="body2"
          textAlign="center"
          sx={{
            fontSize: "0.75rem",
            lineHeight: 1.4,
            opacity: 0.8,
            color: "#fff",
          }}
        >
          Application Version: v1.0.0
          <br />
          &copy; 2026 Genesys
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        bgcolor: "#f9f8f5",
        overflow: "hidden",
      }}
    >
      {isMobile && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 1300,
            bgcolor: "white",
            boxShadow: 2,
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#23395d",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#23395d",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          height: "100vh",
          bgcolor: "#f9f8f5",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>{children}</Box>
      </Box>
      <ComingSoonModal />
    </Box>
  );
}
